import { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Building2, Check, Info, Lock, RotateCcw } from 'lucide-react';
import { FraisParcConfigItemDto, ModeCalculFraisParc, ModeCalculTarification } from 'api/funnel';
import { funnelsAPI } from 'services/swagger-api';
import { InputText } from 'components/forms/InputText';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { useFunnelDetail } from '../FunnelDetailContext';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { BlockCard } from 'back-office/shared/components/BlockCard';
import { MODE_CALCUL_FRAIS_PARC_OPTIONS, MODE_MONTANT_PARC_OPTIONS } from '../funnelEnums';
import { DASH, formatAmount, formatRate } from '../funnelFormat';

interface DraftConfig {
  modeCalcul: ModeCalculFraisParc;
  montantParc: string;
  modeMontantParc: ModeCalculTarification;
}

const toDraft = (item: FraisParcConfigItemDto): DraftConfig => ({
  modeCalcul: item.modeCalcul ?? ModeCalculFraisParc.ParBien,
  montantParc:
    item.montantParc === null || item.montantParc === undefined ? '' : String(item.montantParc),
  modeMontantParc: item.modeMontantParc ?? ModeCalculTarification.MontantFixeHT,
});

const isOncePerPark = (mode: ModeCalculFraisParc) => mode === ModeCalculFraisParc.UneFoisParParc;

/** Tarif unitaire du frais, rappelé pour situer le montant parc saisi. */
const unitSummary = (item: FraisParcConfigItemDto): string => {
  if (item.modeCalculTarif === ModeCalculTarification.Taux) return formatRate(item.taux);
  if (item.modeCalculTarif === ModeCalculTarification.Expression) return 'Expression';
  if (item.modeCalculTarif === ModeCalculTarification.MontantFixeHT) {
    return `${formatAmount(item.montantUnitaire)} HT`;
  }
  return formatAmount(item.montantUnitaire);
};

/**
 * Bloc « Parc immobilier » : comportement de chaque frais quand le contrat porte
 * plusieurs biens.
 *
 * Chaque frais est une carte autonome, avec un choix binaire présenté en deux boutons
 * plutôt qu'en liste déroulante — l'option retenue se lit sans ouvrir quoi que ce soit.
 * Le pseudo-tableau à quatre colonnes de la version précédente devenait illisible dès
 * que les libellés dépassaient la largeur de colonne.
 */
export const ParkBlock: FC = () => {
  const { tunnelId, invalidatePricing } = useFunnelDetail();
  const [drafts, setDrafts] = useState<Record<string, DraftConfig>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [itemToReset, setItemToReset] = useState<FraisParcConfigItemDto | null>(null);

  const fetchConfig = useCallback(
    () => funnelsAPI.getFunnelParkFeesConfig({ tunnelId }),
    [tunnelId]
  );
  const { data, isLoading, error, reload } = useAsyncResource(fetchConfig);

  const items = data?.items ?? [];

  const draftFor = (item: FraisParcConfigItemDto): DraftConfig =>
    drafts[item.remunerationTunnelId] ?? toDraft(item);

  const updateDraft = (item: FraisParcConfigItemDto, patch: Partial<DraftConfig>) =>
    setDrafts((current) => ({
      ...current,
      [item.remunerationTunnelId]: { ...draftFor(item), ...patch },
    }));

  const clearDraft = (remunerationTunnelId: string) =>
    setDrafts((current) => {
      const next = { ...current };
      delete next[remunerationTunnelId];
      return next;
    });

  const save = async (item: FraisParcConfigItemDto) => {
    const draft = draftFor(item);
    const oncePerPark = isOncePerPark(draft.modeCalcul);

    if (oncePerPark && item.isFraisVariable && draft.montantParc.trim() === '') {
      toast.error(
        'Ce frais est variable : le montant parc est obligatoire en mode « une seule fois ».'
      );
      return;
    }

    setSavingId(item.remunerationTunnelId);
    try {
      const response = await funnelsAPI.saveFunnelParkFeeConfig({
        tunnelId,
        remunerationTunnelId: item.remunerationTunnelId,
        saveFraisParcConfigCommand: {
          modeCalcul: draft.modeCalcul,
          montantParc: oncePerPark && draft.montantParc !== '' ? Number(draft.montantParc) : null,
          modeMontantParc: oncePerPark ? draft.modeMontantParc : undefined,
        },
      });

      if (response.isSuccess) {
        toast.success('Configuration enregistrée');
        clearDraft(item.remunerationTunnelId);
        await reload();
        invalidatePricing();
      } else {
        toast.error(response.errorMessage || "L'enregistrement a échoué");
      }
    } catch (err) {
      console.error('Enregistrement de la configuration parc impossible', err);
      toast.error("L'enregistrement a échoué");
    } finally {
      setSavingId(null);
    }
  };

  const reset = async () => {
    if (!itemToReset) return;

    setSavingId(itemToReset.remunerationTunnelId);
    try {
      const response = await funnelsAPI.resetFunnelParkFeeConfig({
        tunnelId,
        remunerationTunnelId: itemToReset.remunerationTunnelId,
      });

      if (response.isSuccess) {
        toast.success('Configuration réinitialisée');
        clearDraft(itemToReset.remunerationTunnelId);
        await reload();
        invalidatePricing();
      } else {
        toast.error(response.errorMessage || 'La réinitialisation a échoué');
      }
    } catch (err) {
      console.error('Réinitialisation impossible', err);
      toast.error('La réinitialisation a échoué');
    } finally {
      setSavingId(null);
      setItemToReset(null);
    }
  };

  return (
    <>
      <BlockCard
        title="Parc immobilier"
        description="Comportement de chaque frais quand un contrat porte plusieurs biens"
        icon={<Building2 className="h-4 w-4" />}
        isLoading={isLoading}
        error={error}
        isEmpty={items.length === 0}
        emptyLabel="Aucun frais configurable pour le parc sur ce tunnel"
      >
        <p className="mb-4 flex items-start gap-2 rounded-lg bg-sky-50 px-4 py-2.5 text-xs text-sky-900">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          Sans configuration, le prix d'un parc est la somme exacte des tarifs de chacun de ses
          biens.
        </p>

        <div className="flex flex-col gap-3">
          {items.map((item) => {
            const draft = draftFor(item);
            const locked = item.isCourtierConnecte === true;
            const oncePerPark = isOncePerPark(draft.modeCalcul);
            const isDirty = Boolean(drafts[item.remunerationTunnelId]);
            const isSaving = savingId === item.remunerationTunnelId;

            return (
              <article
                key={item.remunerationTunnelId}
                className={`rounded-xl border transition-colors ${
                  isDirty ? 'border-primary/40 bg-primary/[0.03]' : 'border-gray-200 bg-white'
                }`}
              >
                <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    {locked && <Lock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />}
                    <span className="truncate text-sm font-semibold text-zinc-800">
                      {item.libelle || DASH}
                    </span>
                    <span className="flex-shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                      {unitSummary(item)}
                    </span>
                  </div>

                  {item.estExplicite ? (
                    <span className="flex-shrink-0 rounded bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      configuré
                    </span>
                  ) : (
                    <span className="flex-shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                      par défaut
                    </span>
                  )}
                </header>

                {locked ? (
                  <p className="px-4 py-3 text-xs italic text-gray-500">
                    Frais « courtier connecté » : le montant saisi au comparateur est compté une
                    seule fois pour le parc, automatiquement.
                  </p>
                ) : (
                  <div className="flex flex-col gap-3 px-4 py-3">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {MODE_CALCUL_FRAIS_PARC_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => updateDraft(item, { modeCalcul: option.value })}
                          className={`rounded-lg border px-3 py-2 text-left transition-all ${
                            draft.modeCalcul === option.value
                              ? 'border-primary bg-primary/10 ring-1 ring-primary'
                              : 'border-gray-200 hover:border-primary/40 hover:bg-gray-50'
                          }`}
                        >
                          <span className="block text-sm font-medium text-zinc-800">
                            {option.label}
                          </span>
                          {option.hint && (
                            <span className="mt-0.5 block text-[11px] leading-tight text-gray-400">
                              {option.hint}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {oncePerPark && (
                      <div className="flex flex-wrap items-end gap-2 rounded-lg bg-gray-50 p-3">
                        <div className="min-w-[8rem] flex-1">
                          <InputText
                            name={`montant-${item.remunerationTunnelId}`}
                            label="Montant pour le parc"
                            type="number"
                            value={draft.montantParc}
                            onChange={(event) =>
                              updateDraft(item, { montantParc: event.target.value })
                            }
                            placeholder={
                              item.montantUnitaire !== null && item.montantUnitaire !== undefined
                                ? String(item.montantUnitaire)
                                : '0'
                            }
                          />
                        </div>
                        <div className="flex gap-1 pb-1">
                          {MODE_MONTANT_PARC_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => updateDraft(item, { modeMontantParc: option.value })}
                              className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                                draft.modeMontantParc === option.value
                                  ? 'border-primary bg-primary text-white'
                                  : 'border-gray-200 text-gray-600 hover:border-primary/40'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-end gap-2">
                      {item.estExplicite && (
                        <button
                          type="button"
                          onClick={() => setItemToReset(item)}
                          disabled={isSaving}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 transition-colors hover:bg-orange-50 disabled:opacity-40"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Réinitialiser
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => save(item)}
                        disabled={!isDirty || isSaving}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Enregistrer
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </BlockCard>

      <ConfirmationOverlay
        isOpen={Boolean(itemToReset)}
        onClose={() => setItemToReset(null)}
        onConfirm={reset}
        isLoading={savingId !== null}
        title="Réinitialiser cette configuration ?"
        description={`Le frais « ${itemToReset?.libelle ?? ''} » reprendra le comportement par défaut de sa nature.`}
        confirmLabel="Réinitialiser"
      />
    </>
  );
};
